import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useUser } from '@/allauth_auth/hooks.js';
import { logger } from '@/lib/logger';
import { getPropertyValue } from '@/types/crm';

import { createCRMObjectClient } from '../core/CRMObjectClient';

export interface Team {
  entity_id: string;
  display_name: string;
  description: string;
  member_count: number;
  last_event_seq: number;
  entity_type_name: 'ahoy_team';
}

export const TEAMS_QUERY_KEY = (
  userId: number | null | undefined,
  orgId: number | null | undefined
) => ['teams', userId, orgId] as const;

const STALE_TIME_MS = 5 * 60 * 1000;
const EMPTY_TEAMS: readonly Team[] = Object.freeze([]);

function getStatusCode(error: unknown): number | undefined {
  if (error && typeof error === 'object' && 'statusCode' in error) {
    const code = (error as { statusCode?: unknown }).statusCode;
    return typeof code === 'number' ? code : undefined;
  }
  return undefined;
}

export function useTeams(orgId: number | null | undefined): {
  teams: readonly Team[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => Promise<unknown>;
} {
  const user = useUser() as { id?: number } | null;
  const userId = user?.id;

  const result = useQuery({
    queryKey: TEAMS_QUERY_KEY(userId, orgId),
    queryFn: async (): Promise<Team[]> => {
      try {
        const response = await createCRMObjectClient(
          orgId as number,
          'ahoy_team'
        ).list(
          { properties: ['ahoy_display_name', 'ahoy_description'] },
          {
            include_associations: {
              ahoy_team_to_user: { properties: [] },
            },
          }
        );

        const teams: Team[] = response.results.map(obj => {
          const displayName =
            getPropertyValue<string>(obj.properties?.ahoy_display_name) ?? '';
          const description =
            getPropertyValue<string>(obj.properties?.ahoy_description) ?? '';
          const memberCount =
            obj.associations?.filter(
              a => a.association_type_name === 'ahoy_team_to_user'
            ).length ?? 0;

          return {
            entity_id: obj.entity_id,
            display_name: displayName,
            description,
            member_count: memberCount,
            last_event_seq: obj.last_event_seq,
            entity_type_name: 'ahoy_team' as const,
          };
        });

        teams.sort((a, b) =>
          a.display_name
            .toLowerCase()
            .localeCompare(b.display_name.toLowerCase())
        );

        return teams;
      } catch (error) {
        logger.error('Failed to fetch teams', error as Error, {
          component: 'useTeams',
          orgId,
          userId,
          status: getStatusCode(error),
        });
        throw error;
      }
    },
    staleTime: STALE_TIME_MS,
    enabled: !!(orgId && userId),
    retry: (failureCount: number, error: Error) => {
      const status = getStatusCode(error);
      if (typeof status === 'number' && status >= 400 && status < 500) {
        return false;
      }
      return failureCount < 1;
    },
  });

  const { refetch } = result;
  return useMemo(
    () => ({
      teams: result.data ?? EMPTY_TEAMS,
      isLoading: result.isLoading,
      isError: result.isError,
      error: (result.error as Error | null) ?? null,
      refetch,
    }),
    [result.data, result.isLoading, result.isError, result.error, refetch]
  );
}
